/**
 * Hub de Fretes — configuração do motor de cotação (FreightHub) em [Shipping].[FreightHub*].
 * Tela: Configuração → Transporte → Transportadoras → aba "Hub de Fretes".
 * Dinheiro em centavos, percentuais em basis points (1% = 100). O ERPApi valida e grava o
 * documento inteiro e pede ao hub para recarregar.
 */

export type FreightHubProviderKey = 'correios' | 'fmtransportes' | 'melhorenvio' | 'viverorganizer';

export const FREIGHT_HUB_PROVIDER_KEYS: FreightHubProviderKey[] = ['correios', 'fmtransportes', 'melhorenvio', 'viverorganizer'];

/** O storefront espera 15 s pela cotação: o orçamento do hub precisa caber antes. */
export const FREIGHT_HUB_QUOTE_BUDGET_MAX_SECONDS = 14;

export interface FreightHubConfig {
  markup_bps: number;
  min_shipping_cost_cents: number;
  packing_days: number;
  declared_value_cap_cents: number;
  excess_surcharge_per_1000_cents: number;
  provider_timeout_seconds: number;
  quote_budget_seconds: number;
  quote_cache_minutes: number;
  config_refresh_seconds: number;
  catalog_refresh_seconds: number;
  breaker_failures: number;
  breaker_open_seconds: number;
  default_company_id: number;
  origin_zip: string;
  packing_strategy: 'legacy' | 'catalog';
  /** ocupação alvo da caixa na estratégia catalog (8500 = 85%) */
  pack_fill_target_bps: number;
  /** fatores sobre o volume com folga quando o SKU não tem estatística (1500 = 15%) */
  pack_factor_flat_stack_bps: number;
  pack_factor_flat_upright_bps: number;
  pack_factor_rigid_bps: number;
  /** amostras mínimas para usar o volume efetivo aprendido do SKU */
  pack_min_samples: number;
  /** true = reproduz a regra do ProductsAPI na escolha da opção "mais rápida" (paridade) */
  legacy_fastest_rule: boolean;
  /** só leitura: preenchido pelo ERPApi; não é enviado no PUT */
  updated_at?: string;
  updated_by?: string | null;
}

export interface FreightHubProviderConfig {
  provider_key: FreightHubProviderKey;
  display_name: string;
  enabled: boolean;
  enabled_erp: boolean;
  enabled_site: boolean;
  timeout_seconds: number | null;
  endpoint_url: string | null;
  quote_cache_minutes: number | null;
  apply_min_shipping_cost: boolean;
  apply_markup: boolean;
  apply_surcharge: boolean;
  add_packing_days: boolean;
  updated_at?: string;
  updated_by?: string | null;
}

export interface FreightHubZipRestriction {
  id: number;
  provider_key: FreightHubProviderKey;
  zip_from: string;
  zip_to: string;
  reason: string | null;
  enabled: boolean;
}

export interface FreightHubOwnFleetConfig {
  service_name: string;
  delivery_days: number;
  included_packages: number;
  additional_package_fee_cents: number;
  percent_fee_bps: number;
  updated_at?: string;
  updated_by?: string | null;
}

export interface FreightHubOwnFleetZipRange {
  id: number;
  zip_prefix_from: number;
  zip_prefix_to: number;
  price_cents: number;
  label: string | null;
  enabled: boolean;
}

export interface FreightHubConfigDocument {
  config: FreightHubConfig;
  providers: FreightHubProviderConfig[];
  zip_restrictions: FreightHubZipRestriction[];
  own_fleet: FreightHubOwnFleetConfig;
  own_fleet_ranges: FreightHubOwnFleetZipRange[];
  boxes: FreightHubBox[];
  service_labels: FreightHubServiceLabel[];
}

export interface FreightHubReloadResult {
  changed: boolean;
  source: string;
  loaded_at: string;
  message: string;
}

export interface FreightHubSaveResult {
  document: FreightHubConfigDocument;
  hub_reloaded: boolean;
  hub_message: string;
}

/** Caixa do catálogo ([Shipping].[FreightHubBox]); medidas internas em cm, pesos em kg. */
export interface FreightHubBox {
  id: number;
  name: string;
  inner_height_cm: number;
  inner_width_cm: number;
  inner_length_cm: number;
  tare_kg: number;
  max_weight_kg: number;
  /** desempate entre caixas de volume parecido (menor = preferida) */
  priority: number;
  enabled: boolean;
}

export type FreightHubLabelRole = 'first' | 'cheapest' | 'fastest' | 'default' | 'days_suffix';

export const FREIGHT_HUB_LABEL_ROLES: FreightHubLabelRole[] = ['first', 'cheapest', 'fastest', 'default', 'days_suffix'];

/** Rótulo exibido ao cliente. provider_key "*" só no papel days_suffix. */
export interface FreightHubServiceLabel {
  provider_key: FreightHubProviderKey | '*';
  role: FreightHubLabelRole;
  template: string;
}

export interface FreightHubSkuPackingStat {
  sku: string;
  effective_volume_cm3: number;
  samples: number;
  method: 'single' | 'manual' | string;
  computed_at: string;
  gross_volume_cm3?: number | null;
}

export interface FreightHubPackingWeek {
  week_start: string;
  strategy: 'legacy' | 'catalog' | string;
  orders: number;
  equal: number;
  over: number;
  under: number;
  predicted_boxes: number;
  actual_boxes: number;
  equal_pct: number;
}

/** GET /shipping/freight-hub-config/packing-report */
export interface FreightHubPackingReport {
  weeks: FreightHubPackingWeek[] | null;
  top_skus: FreightHubSkuPackingStat[] | null;
  stats_at?: string | null;
  stats_sku_count: number;
}
